---
title: Skills, MCP & plugins
lede: The skills, tool servers and plugins a session's agent is given on top of what its CLI ships with.
order: 37
part: Shape the machine
---

This page lists what a session's agent can use beyond its own CLI: skills, MCP servers and plugins. Airlock writes each entry that is on into the agent's own configuration inside the session's machine when the session starts.

Set it for every workspace under **Settings › Skills, MCP & plugins**, or for one workspace under **Workspace settings › Skills, MCP & plugins**. Both Claude Code and Codex receive it. On a workspace's page, **Override** replaces the application's list whole, starting from a copy of it.

![The Skills, MCP & plugins page](media/settings-extensions.webp)

### Skills

A skill is a folder the agent can call by name.

| Column | What to write |
|---|---|
| **Name** | The name the agent calls it by |
| **Path** | A folder from the session's workspace root, such as a folder in one of your repositories, or the address of a public GitHub repository or one of its folders (`https://github.com/owner/repo/tree/main/skills/x`) |

A GitHub skill is downloaded by Airlock and copied in at boot. The sandbox never fetches it, so its network allowlist is not widened.

### MCP servers

An MCP server is a tool server the agent may call. It is started inside the session by the command you write, so it reaches only what the session reaches.

| Column | What to write |
|---|---|
| **Name** | The name the agent calls it by |
| **Command** | The command line that starts it |
| **Environment** | Variables passed to it, under the row's fold |

:::warn No credentials here
A variable that looks like a credential is refused: this list can be published to an organization. Put the token in the workspace's secrets instead.
:::

### Plugins

| Column | What to write |
|---|---|
| **Plugin** | Its name, as its marketplace names it |
| **Marketplace** | The marketplace it comes from |
| **Source** | Where the marketplace lives: `owner/repo` or an `https` URL |

Airlock registers the marketplace in the session and turns the plugin on, as the agent's own `/plugin` would.

### Turning an entry on or off

Each row has a switch, **Give <name> to the agent**. An entry that is off stays listed but is not written into the session. The button at the end of a row removes it when you save.

Names use letters, digits, `.`, `_` and `-`, up to 64 characters.

### Limits

| List | At most |
|---|---|
| Skills | 40 |
| MCP servers | 20, each with up to 24 arguments and 24 variables |
| Plugins | 20 |

### When something does not arrive

Anything that could not be given to the agent, such as a skill whose folder is missing, is named on the session's startup timeline and in an error toast. Changes apply to sessions started after you save.

On an organization's workspace the list may be set by the organization. It is then read-only here, marked **Set by <organization>**.

An organization can also keep a library of approved entries. A workspace's page lists them, with **Add to this workspace**. If the organization allows only its library, a session given anything else is refused before it starts.
